'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import Navbar from './Navbar';
import SessionShell from '@/components/layout/SessionShell';
import { useAuth } from '@/lib/auth-context';

export default function LayoutWithNavbar({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isAuthenticated } = useAuth();

  // Auth screens carry their own split-panel layout, and public certificate
  // verification pages render standalone. Neither belongs inside the app shell -
  // /login was rendering inside the sidebar for an already-signed-in visitor.
  const AUTH_ROUTES = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/accept-invite',
    '/verify-email-success',
    '/verify-email-failed',
  ];
  const hideChrome =
    AUTH_ROUTES.some((r) => pathname === r || pathname?.startsWith(`${r}/`)) ||
    /^\/0x[a-fA-F0-9]{40}$/.test(pathname || '') ||
    /^\/[^/]+\/0x[a-fA-F0-9]{40}$/.test(pathname || '');

  /**
   * Everything except these needs a session. Hiding the nav link was not enough:
   * an anonymous visitor typing /certificates got a skeleton that never
   * resolved, because the view waits on a user that will never arrive.
   *
   * AuthProvider withholds children until it has finished checking, so an
   * unauthenticated render here is genuine, not a hydration gap.
   */
  const PUBLIC_ROUTES = ['/', '/verify', '/pricing', ...AUTH_ROUTES];
  const isPublic =
    hideChrome ||
    PUBLIC_ROUTES.some((r) => pathname === r || pathname?.startsWith(`${r}/`)) ||
    /^\/0x[a-fA-F0-9]{40}$/.test(pathname || '') ||
    /^\/[^/]+\/0x[a-fA-F0-9]{40}$/.test(pathname || '');

  useEffect(() => {
    if (!isAuthenticated && !isPublic && pathname) {
      router.replace(`/login?next=${encodeURIComponent(pathname)}`);
    }
  }, [isAuthenticated, isPublic, pathname, router]);

  if (hideChrome) {
    return <>{children}</>;
  }

  if (!isAuthenticated && !isPublic) {
    // Render nothing while the redirect above runs, rather than a skeleton that
    // can never finish loading.
    return null;
  }

  if (user?.role === 'OPERATOR' || user?.role === 'ADMIN' || user?.role === 'USER') {
    return <SessionShell>{children}</SessionShell>;
  }

  return (
    <>
      <Navbar />
      {children}
    </>
  );
}
